import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import api from "./services/api";
import * as XLSX from "xlsx";

const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  if (typeof timeStr === 'number') return Math.round(timeStr * 60);
  const s = String(timeStr).trim();
  if (!s) return 0;
  if (s.includes(':')) {
    const parts = s.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  }
  const num = parseFloat(s);
  if (!isNaN(num)) {
    return Math.round(num * 60);
  }
  return 0;
};

const minutesToTime = (totalMinutes) => {
  if (isNaN(totalMinutes) || totalMinutes <= 0) return '00:00';
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const roundTo5Minutes = (minutes) => {
  if (isNaN(minutes) || minutes <= 0) return 0;
  return Math.floor(minutes / 5) * 5;
};

const calculateDayOt = (arrStr, depStr) => {
  const arrM = timeToMinutes(arrStr);
  const depM = timeToMinutes(depStr);
  if (arrM === 0 || depM === 0 || depM <= arrM) return '00:00';
  if (depM - arrM <= 270) return '00:00'; // Under 4:30 hrs has no OT

  // Company OT Policy:
  // Step 1 — Check buffer: If Logout <= 5:30 PM (1050 mins) -> OT = 0
  if (depM <= 1050) {
    return '00:00';
  }

  // Step 2 — Gross OT = Logout Time - 5:00 PM (1020 mins)
  const grossOtMins = depM - 1020;

  // Step 3 — Deduct late arrival = max(0, Actual Arrival - 8:30 AM (510 mins))
  const lateDeduction = arrM > 510 ? (arrM - 510) : 0;

  // Step 4 — Final OT = max(0, Gross OT - Late Deduction), rounded to 5 mins
  const netOtMins = Math.max(0, grossOtMins - lateDeduction);
  const rounded = roundTo5Minutes(netOtMins);

  return rounded > 0 ? minutesToTime(rounded) : '00:00';
};

const getDayShortMinutes = (day) => {
  if (!day) return 0;
  const arrM = timeToMinutes(day.arrive);
  const depM = timeToMinutes(day.depart);
  if (arrM === 0 || depM === 0 || depM <= arrM) return 0;
  const worked = depM - arrM;
  const status = String(day.status || '').trim().toUpperCase();
  const isHalf = (status === 'P/2' || status === 'HL' || status === '0.5' || status === 'HD');
  
  // Normal half day (worked <= 4:30 hrs and marked half) is already compensated by 0.5 paid day
  if (isHalf && worked <= 270) return 0;
  
  // Offs, Leaves, Absents without punches have no short hours
  if (status === 'WO' || status === 'OFF' || status === 'A' || status === 'L') return 0;

  // Standard shift expectation is 8:30 (510 minutes)
  if (worked < 510) {
    return 510 - worked;
  }
  return 0;
};

const getDayDateInfo = (dayNum, currentMonthYear) => {
  if (!currentMonthYear) {
    return { dayNum, weekday: '', isHoliday: false, fullDate: `Day ${dayNum}` };
  }
  try {
    const parts = currentMonthYear.split(' ');
    if (parts.length >= 2) {
      const monthName = parts[0];
      const year = parseInt(parts[1], 10);
      const monthIdx = new Date(Date.parse(monthName + " 1, 2012")).getMonth();
      if (!isNaN(monthIdx) && !isNaN(year)) {
        const d = new Date(year, monthIdx, dayNum);
        const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
        const isHoliday = d.getDay() === 2; // Tuesday is the weekly off
        return {
          dayNum,
          weekday,
          isHoliday,
          fullDate: `${String(dayNum).padStart(2, '0')} ${monthName.slice(0, 3)} (${weekday})`
        };
      }
    }
  } catch (e) {}
  return { dayNum, weekday: '', isHoliday: false, fullDate: `Day ${dayNum}` };
};

const getStatusWeight = (status) => {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'P' || s === 'POW' || s === 'PR') return 1;
  if (s === 'WO' || s === 'OFF') return 0; // Weekly off is NOT paid and NOT present
  if (s === 'P/2' || s === 'HL' || s === '0.5' || s === 'HD') return 0.5;
  if (s === 'A' || s === 'AB' || s === 'L' || s === 'LV') return 0;
  return 0;
};

const getDayAlertInfo = (day) => {
  if (!day) return null;
  const status = String(day.status || '').trim().toUpperCase();
  const arrM = timeToMinutes(day.arrive);
  const depM = timeToMinutes(day.depart);

  // Normal off / leave / absent without punch
  if ((status === 'WO' || status === 'OFF' || status === 'A' || status === 'L' || !status) && arrM === 0 && depM === 0) {
    return null;
  }

  // 1. Missing punch anomalies
  if (arrM > 0 && depM === 0) {
    return { type: 'alert', label: 'ALERT', color: 'red', reason: 'Missing Departure punch (Single punch)' };
  }
  if (arrM === 0 && depM > 0) {
    return { type: 'alert', label: 'ALERT', color: 'red', reason: 'Missing Arrival punch (Single punch)' };
  }
  if (arrM > 0 && depM > 0 && depM < arrM) {
    return { type: 'alert', label: 'ALERT', color: 'red', reason: 'Abnormal: Depart time is before Arrive time' };
  }

  // 2. Status mismatches
  if ((status === 'P' || status === 'POW') && arrM === 0 && depM === 0) {
    return { type: 'alert', label: 'ALERT', color: 'red', reason: 'Marked Present but zero biometric punches' };
  }
  if (status === 'A' && (arrM > 0 || depM > 0)) {
    return { type: 'alert', label: 'ALERT', color: 'red', reason: 'Marked Absent but employee punched biometric' };
  }

  // 3. Worked duration analysis when both punches are present
  if (arrM > 0 && depM > 0 && depM > arrM) {
    const worked = depM - arrM;
    const isMarkedHalf = (status === 'P/2' || status === 'HL' || status === '0.5');

    // 3a. Marked Half Day (P/2), but worked more than 4:30 hours (> 270 mins)
    if (isMarkedHalf && worked > 270) {
      const machineMismatch = (day.workHrs && Math.abs(worked - timeToMinutes(day.workHrs)) > 60)
        ? ` (Biometric logged WorkHrs: ${day.workHrs})`
        : '';
      if (worked >= 510) {
        return {
          type: 'alert',
          label: 'STATUS ERROR',
          color: 'red',
          reason: `Status Error: Punches show ${minutesToTime(worked)} (Full Day: ${day.arrive} to ${day.depart}) but marked Half Day (P/2)!${machineMismatch}`
        };
      } else {
        return {
          type: 'alert',
          label: 'STATUS ERROR',
          color: 'red',
          reason: `Status Error: Worked ${minutesToTime(worked)} (> 4:30 hrs) but marked Half Day (P/2)!${machineMismatch}`
        };
      }
    }

    // 3b. Worked 4 to 4:30 hours (<= 270 mins): give error of half day if not marked P/2
    if (worked <= 270) {
      if (!isMarkedHalf) {
        return {
          type: 'half_day_error',
          label: 'HALF DAY ERROR',
          color: 'amber',
          reason: `Half Day Error: Worked ${minutesToTime(worked)} (4 - 4:30 hrs) -> Marked ${status || 'None'} instead of P/2`
        };
      }
      return null;
    }

    // 3c. Between 4:30 and 8:30 hours worked (e.g. 5 hours, 6 hours, 7 hours, 8 hours): mark alert
    if (worked < 510) {
      const shortBy = 510 - worked;
      return {
        type: 'alert',
        label: 'ALERT',
        color: 'red',
        reason: `Alert: Worked ${minutesToTime(worked)} (Under 8:30 hrs - Short by ${minutesToTime(shortBy)})`
      };
    }
  }

  return null;
};

const normalizeTimeInput = (val) => {
  if (!val) return '';
  const s = String(val).trim();
  if (!s) return '';
  if (s.includes(':')) {
    const parts = s.split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return s;
};

const normalizeAndRoundData = (employees) => {
  return (employees || []).map((emp, idx) => {
    const empId = emp.id || `EMP-${emp.empCode || idx}`;
    let totalActMins = 0;
    let totalCalcMins = 0;
    let totalShortMins = 0;
    let totalPres = 0;
    let totalAbs = 0;
    let totalWo = 0;

    const updatedDays = (emp.days || []).map(d => {
      let actOt = String(d.actualOt || '').trim();
      if (!actOt && d.ot && !d.calculatedOt) {
        actOt = d.ot;
      }
      if (!actOt) actOt = '00:00';

      let calcOt = String(d.calculatedOt || d.ot || '00:00').trim();
      
      let actM = timeToMinutes(actOt);
      let calcM = timeToMinutes(calcOt);
      let roundedCalcM = roundTo5Minutes(calcM);

      let dayStatus = d.status;
      const arrM = timeToMinutes(d.arrive);
      const depM = timeToMinutes(d.depart);

      // If worked duration is 4:30 hours or less (<= 270 mins), mark as half day (P/2) and no OT
      if (arrM > 0 && depM > 0 && depM > arrM && (depM - arrM) <= 270) {
        dayStatus = 'P/2';
        roundedCalcM = 0;
        calcOt = '00:00';
        actM = 0;
        actOt = '00:00';
      }

      totalActMins += actM;
      totalCalcMins += roundedCalcM;
      totalShortMins += getDayShortMinutes({ ...d, status: dayStatus });

      const w = getStatusWeight(dayStatus);
      totalPres += w;
      const st = String(dayStatus || '').trim().toUpperCase();
      if (st === 'A' || st === 'AB' || st === 'L' || st === 'LV') totalAbs += 1;
      else if (st === 'P/2' || st === 'HL' || st === '0.5' || st === 'HD') totalAbs += 0.5;
      else if (st === 'WO' || st === 'OFF') totalWo += 1;

      return {
        ...d,
        status: dayStatus,
        actualOt: actM > 0 ? minutesToTime(actM) : '00:00',
        calculatedOt: roundedCalcM > 0 ? minutesToTime(roundedCalcM) : '00:00',
        ot: roundedCalcM > 0 ? minutesToTime(roundedCalcM) : '00:00'
      };
    });

    const paidDaysVal = (totalPres % 1 === 0) ? String(totalPres) : String(totalPres.toFixed(1));
    const absVal = (totalAbs % 1 === 0) ? String(totalAbs) : String(totalAbs.toFixed(1));

    return {
      ...emp,
      id: empId,
      days: updatedDays,
      paidDays: paidDaysVal,
      present: paidDaysVal,
      absent: absVal,
      wo: String(totalWo),
      totalActualOt: minutesToTime(totalActMins),
      totalCalculatedOt: minutesToTime(totalCalcMins),
      totalOvertime: minutesToTime(totalCalcMins),
      totalShortHrs: minutesToTime(totalShortMins)
    };
  });
};

const empKey = (e) => String(e.empCode || e.id || '');

// Re-derive an employee's summary figures from its days
const recomputeEmployeeTotals = (row, newDays) => {
  const totalCalcMins = newDays.reduce((acc, d) => acc + timeToMinutes(d.calculatedOt || d.ot), 0);
  const totalActMins = newDays.reduce((acc, d) => acc + timeToMinutes(d.actualOt), 0);
  const totalShortMins = newDays.reduce((acc, d) => acc + getDayShortMinutes(d), 0);

  let totalPres = 0;
  let totalAbs = 0;
  let totalWo = 0;
  newDays.forEach(d => {
    totalPres += getStatusWeight(d.status);
    const s = String(d.status || '').trim().toUpperCase();
    if (s === 'A' || s === 'AB' || s === 'L' || s === 'LV') totalAbs += 1;
    else if (s === 'P/2' || s === 'HL' || s === '0.5' || s === 'HD') totalAbs += 0.5;
    else if (s === 'WO' || s === 'OFF') totalWo += 1;
  });

  const paidDays = (totalPres % 1 === 0) ? String(totalPres) : String(totalPres.toFixed(1));
  const absent = (totalAbs % 1 === 0) ? String(totalAbs) : String(totalAbs.toFixed(1));

  return {
    ...row,
    days: newDays,
    totalCalculatedOt: minutesToTime(totalCalcMins),
    totalOvertime: minutesToTime(totalCalcMins),
    totalActualOt: minutesToTime(totalActMins),
    totalShortHrs: minutesToTime(totalShortMins),
    paidDays,
    present: paidDays,
    absent,
    wo: String(totalWo)
  };
};

// Another user saved the month while we were editing: lay only OUR changes (what differs
// from `base`, the copy we loaded/last saved) on top of the latest server copy, so
// neither side's edits are lost.
const mergeLocalChanges = (base, local, server) => {
  const baseMap = new Map((base || []).map(e => [empKey(e), e]));
  const merged = server.slice();
  const indexByKey = new Map(merged.map((e, i) => [empKey(e), i]));

  local.forEach(localEmp => {
    const key = empKey(localEmp);
    const baseEmp = baseMap.get(key);
    const si = indexByKey.get(key);

    if (si === undefined) {
      merged.push(localEmp); // employee exists only in our copy
      indexByKey.set(key, merged.length - 1);
      return;
    }
    if (!baseEmp || localEmp === baseEmp) return; // untouched by us

    const serverEmp = merged[si];
    const newDays = (serverEmp.days || []).slice();
    const dayIndex = new Map(newDays.map((d, i) => [d.day, i]));
    const baseDays = new Map((baseEmp.days || []).map(d => [d.day, d]));
    let daysChanged = false;

    (localEmp.days || []).forEach(localDay => {
      const baseDay = baseDays.get(localDay.day);
      const changedFields = Object.keys(localDay).filter(f => !baseDay || localDay[f] !== baseDay[f]);
      if (changedFields.length === 0) return;
      const patch = {};
      changedFields.forEach(f => { patch[f] = localDay[f]; });
      const i = dayIndex.get(localDay.day);
      if (i === undefined) {
        newDays.push(localDay);
        dayIndex.set(localDay.day, newDays.length - 1);
      } else {
        newDays[i] = { ...newDays[i], ...patch };
      }
      daysChanged = true;
    });

    if (daysChanged) {
      merged[si] = recomputeEmployeeTotals(serverEmp, newDays);
    } else {
      // only employee-level cells (e.g. a hand-edited total) changed
      const patch = {};
      Object.keys(localEmp).forEach(f => {
        if (f !== 'days' && localEmp[f] !== baseEmp[f]) patch[f] = localEmp[f];
      });
      if (Object.keys(patch).length > 0) merged[si] = { ...serverEmp, ...patch };
    }
  });

  return merged;
};

function EmployeeAuditModal({
  employee,
  allEmployees,
  onSelectEmployee,
  onClose,
  onUpdateDay,
  currentMonthYear,
  autoSaveStatus,
  showNotification
}) {
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'issues' | 'present' | 'half' | 'absent' | 'wo'

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!employee) return null;

  const currentIndex = allEmployees.findIndex(e => e.id === employee.id);
  const prevEmp = currentIndex > 0 ? allEmployees[currentIndex - 1] : null;
  const nextEmp = currentIndex < allEmployees.length - 1 ? allEmployees[currentIndex + 1] : null;

  const days = employee.days || [];
  const daysWithAlerts = days.filter(d => getDayAlertInfo(d) !== null);
  const alertCount = daysWithAlerts.length;

  const shortDays = days.filter(d => getDayShortMinutes(d) > 0);
  const shortDaysCount = shortDays.length;
  const totalShortMins = days.reduce((acc, d) => acc + getDayShortMinutes(d), 0);
  const totalShortHrsStr = minutesToTime(totalShortMins);

  const presentCount = days.filter(d => d.status === 'P' || d.status === 'POW').length;
  const halfCount = days.filter(d => d.status === 'P/2' || d.status === 'HL' || d.status === '0.5').length;
  const absentCount = days.filter(d => d.status === 'A' || d.status === 'L').length;
  const woCount = days.filter(d => d.status === 'WO' || d.status === 'OFF').length;

  let displayedDays = days;
  if (activeTab === 'issues') displayedDays = daysWithAlerts;
  else if (activeTab === 'short') displayedDays = shortDays;
  else if (activeTab === 'present') displayedDays = days.filter(d => d.status === 'P' || d.status === 'POW');
  else if (activeTab === 'half') displayedDays = days.filter(d => d.status === 'P/2' || d.status === 'HL' || d.status === '0.5');
  else if (activeTab === 'absent') displayedDays = days.filter(d => d.status === 'A' || d.status === 'L');
  else if (activeTab === 'wo') displayedDays = days.filter(d => d.status === 'WO' || d.status === 'OFF');

  // Batch action: Fix all status errors (worked >= 8:30 but marked P/2)
  const handleFixAllStatusErrors = () => {
    let fixCount = 0;
    days.forEach(d => {
      const arrM = timeToMinutes(d.arrive);
      const depM = timeToMinutes(d.depart);
      const isHalf = (d.status === 'P/2' || d.status === 'HL' || d.status === '0.5');
      if (arrM > 0 && depM > 0 && depM - arrM >= 510 && isHalf) {
        onUpdateDay(employee.id, d.day, { status: 'P' });
        fixCount++;
      }
    });
    if (fixCount > 0) {
      showNotification(`Fixed ${fixCount} status mismatch day(s) to Present (P)!`);
    } else {
      showNotification('No status mismatch errors found to fix.', 'info');
    }
  };

  // Batch action: Recalculate all OT from punches
  const handleRecalculateAllOt = () => {
    let count = 0;
    days.forEach(d => {
      if (d.arrive && d.depart) {
        const autoOt = calculateDayOt(d.arrive, d.depart);
        onUpdateDay(employee.id, d.day, { calculatedOt: autoOt, ot: autoOt });
        count++;
      }
    });
    showNotification(`Recalculated overtime for ${count} day(s) from punches!`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-hidden">
      <div 
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-7xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Modal Header */}
        <div className="p-4 sm:px-6 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
          {/* Employee Identity */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-black text-lg flex items-center justify-center shadow-md">
              {(employee.name || "E").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 tracking-tight capitalize">
                  {employee.name}
                </h2>
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-200">
                  Emp #{employee.empCode}
                </span>
                {currentMonthYear && (
                  <span className="bg-slate-200 text-slate-700 text-xs font-semibold px-2 py-0.5 rounded">
                    {currentMonthYear}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                Individual Employee Attendance Audit & Management
              </p>
            </div>
          </div>

          {/* Employee Switcher & Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => prevEmp && onSelectEmployee(prevEmp.id)}
              disabled={!prevEmp}
              className="px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 shadow-2xs transition-colors cursor-pointer"
              title="Previous Employee"
            >
              <i className="fas fa-chevron-left"></i> Prev
            </button>

            <select
              value={employee.id}
              onChange={(e) => onSelectEmployee(e.target.value)}
              className="text-xs font-semibold bg-white border border-slate-300 text-slate-800 rounded-lg px-2.5 py-1.5 outline-none focus:border-blue-500 max-w-[220px] truncate shadow-2xs cursor-pointer"
            >
              {allEmployees.map(emp => {
                const issues = (emp.days || []).filter(d => getDayAlertInfo(d) !== null).length;
                return (
                  <option key={emp.id} value={emp.id}>
                    #{emp.empCode} - {emp.name} {issues > 0 ? `(⚠️ ${issues})` : ''}
                  </option>
                );
              })}
            </select>

            <button
              onClick={() => nextEmp && onSelectEmployee(nextEmp.id)}
              disabled={!nextEmp}
              className="px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 shadow-2xs transition-colors cursor-pointer"
              title="Next Employee"
            >
              Next <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          {/* Auto-save status & Close */}
          <div className="flex items-center gap-3">
            {autoSaveStatus === 'saving' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 animate-pulse">
                <i className="fas fa-spinner fa-spin text-blue-600"></i> Auto-saving...
              </span>
            )}
            {autoSaveStatus === 'saved' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <i className="fas fa-check-circle text-emerald-600"></i> Auto-saved
              </span>
            )}
            {autoSaveStatus === 'pending' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <i className="fas fa-pen text-amber-600"></i> Unsaved changes...
              </span>
            )}
            {autoSaveStatus === 'error' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-300">
                <i className="fas fa-exclamation-triangle text-red-600"></i> NOT SAVED - retrying
              </span>
            )}
            {autoSaveStatus === 'idle' && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                <i className="fas fa-bolt text-amber-500"></i> Auto-sync on
              </span>
            )}

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-500 flex items-center justify-center transition-colors shadow-2xs font-bold cursor-pointer"
              title="Close modal (Esc)"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* 2. Employee KPI Metric Cards */}
        <div className="p-4 sm:px-6 bg-slate-50/50 border-b border-slate-200 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Paid</span>
            <span className="text-xl font-black text-blue-700 mt-0.5">{employee.paidDays || '0'}</span>
            <span className="text-[10px] text-slate-500 mt-0.5">Present: {employee.present || '0'}</span>
          </div>

          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Present Days</span>
            <span className="text-xl font-black text-emerald-700 mt-0.5">{presentCount}</span>
            <span className="text-[10px] text-emerald-600 mt-0.5">Full Shifts logged</span>
          </div>

          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Half Days</span>
            <span className="text-xl font-black text-indigo-700 mt-0.5">{halfCount}</span>
            <span className="text-[10px] text-slate-500 mt-0.5">P/2 or 4-4:30 hrs</span>
          </div>

          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Absent / WO</span>
            <span className="text-xl font-black text-slate-700 mt-0.5">{absentCount} <span className="text-xs font-normal text-slate-400">/ {woCount} WO</span></span>
            <span className="text-[10px] text-slate-500 mt-0.5">Absent count: {employee.absent || '0'}</span>
          </div>

          <div className="bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Total Calc OT</span>
            <span className="text-xl font-black text-blue-800 mt-0.5">{employee.totalCalculatedOt || '00:00'}</span>
            <span className="text-[10px] text-slate-500 mt-0.5">Actual: {employee.totalActualOt || '00:00'}</span>
          </div>

          <div className={`p-2.5 rounded-xl border shadow-2xs flex flex-col transition-all ${
            totalShortMins > 0 
              ? 'bg-amber-50/90 border-amber-300 ring-2 ring-amber-400/20' 
              : 'bg-white border-slate-200'
          }`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${totalShortMins > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
              Total Short Hrs
            </span>
            <span className={`text-xl font-black mt-0.5 ${totalShortMins > 0 ? 'text-amber-700' : 'text-slate-700'}`}>
              {totalShortHrsStr}
            </span>
            <span className={`text-[10px] mt-0.5 font-medium ${totalShortMins > 0 ? 'text-amber-600' : 'text-slate-500'}`}>
              {shortDaysCount > 0 ? `${shortDaysCount} day${shortDaysCount > 1 ? 's' : ''} < 8:30` : 'No short days'}
            </span>
          </div>

          <div className={`p-2.5 rounded-xl border shadow-2xs flex flex-col transition-all ${
            alertCount > 0 
              ? 'bg-red-50/90 border-red-300 ring-2 ring-red-400/20' 
              : 'bg-emerald-50/90 border-emerald-300'
          }`}>
            <span className={`text-[10px] font-bold uppercase tracking-wider ${alertCount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              Audit Status
            </span>
            <span className={`text-xl font-black mt-0.5 ${alertCount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
              {alertCount > 0 ? `${alertCount} Issues` : 'All Clear'}
            </span>
            <span className={`text-[10px] mt-0.5 font-medium ${alertCount > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {alertCount > 0 ? 'Review & fix below' : '31 days verified'}
            </span>
          </div>
        </div>

        {/* 3. Toolbar: Filter Tabs & Batch Action Tools */}
        <div className="p-3 sm:px-6 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          {/* Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'all'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              All Days <span className="text-[10px] opacity-80 font-normal">({days.length})</span>
            </button>

            <button
              onClick={() => setActiveTab('issues')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'issues'
                  ? 'bg-red-600 text-white shadow-xs'
                  : alertCount > 0
                  ? 'bg-red-100 text-red-700 border border-red-200 hover:bg-red-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <i className="fas fa-exclamation-triangle"></i>
              Issues & Alerts <span className="text-[10px] font-bold">({alertCount})</span>
            </button>

            <button
              onClick={() => setActiveTab('short')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'short'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : shortDaysCount > 0
                  ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <i className="fas fa-clock text-amber-500"></i>
              Short Days <span className="text-[10px] font-bold">({shortDaysCount})</span>
            </button>

            <button
              onClick={() => setActiveTab('present')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'present'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Presents ({presentCount})
            </button>

            <button
              onClick={() => setActiveTab('half')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'half'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Half Days ({halfCount})
            </button>

            <button
              onClick={() => setActiveTab('absent')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'absent'
                  ? 'bg-rose-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Absent / Leave ({absentCount})
            </button>

            <button
              onClick={() => setActiveTab('wo')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === 'wo'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Weekly Offs ({woCount})
            </button>
          </div>

          {/* Batch Tools */}
          <div className="flex items-center gap-2 ml-auto">
            {alertCount > 0 && (
              <button
                onClick={handleFixAllStatusErrors}
                className="px-3 py-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Auto-fix days where punches show full day (>= 8:30) but marked P/2"
              >
                <i className="fas fa-magic text-emerald-600"></i> Fix Status Errors to P
              </button>
            )}

            <button
              onClick={handleRecalculateAllOt}
              className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-300 rounded-lg shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Recalculate calculated OT for all days based on punches"
            >
              <i className="fas fa-sync-alt text-blue-600"></i> Recalc All OT
            </button>
          </div>
        </div>

        {/* 4. Spacious Days Table */}
        <div className="overflow-y-auto flex-1 p-3 sm:px-6">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 bg-slate-100 text-[11px] font-bold text-slate-600 uppercase border-b border-slate-200 shadow-xs z-10">
              <tr>
                <th className="py-2.5 px-3 whitespace-nowrap">Date / Day</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Arrive Punch</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Depart Punch</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Total Worked</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Machine WorkHrs</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Status</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Machine OT</th>
                <th className="py-2.5 px-3 text-center whitespace-nowrap">Calculated OT</th>
                <th className="py-2.5 px-3 whitespace-nowrap">Audit & Quick Fix</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedDays.map(d => {
                const dateInfo = getDayDateInfo(d.day, currentMonthYear);
                const alertInfo = getDayAlertInfo(d);
                const arrM = timeToMinutes(d.arrive);
                const depM = timeToMinutes(d.depart);
                const workedMins = (arrM > 0 && depM > 0 && depM > arrM) ? depM - arrM : 0;
                const lateMins = (arrM > 510) ? arrM - 510 : 0;
                const machineMins = timeToMinutes(d.workHrs);
                const isMachineGlitch = (workedMins > 0 && machineMins > 0 && Math.abs(workedMins - machineMins) > 60);

                return (
                  <tr 
                    key={d.day} 
                    className={`transition-colors ${
                      alertInfo?.color === 'red' 
                        ? 'bg-red-50/70 hover:bg-red-100/70' 
                        : alertInfo?.color === 'amber'
                        ? 'bg-amber-50/70 hover:bg-amber-100/70'
                        : dateInfo.isHoliday
                        ? 'bg-slate-50/90 hover:bg-slate-100'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    {/* Date / Day */}
                    <td className="py-2.5 px-3 font-semibold text-slate-800 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-lg text-xs font-black flex items-center justify-center ${
                          dateInfo.isHoliday 
                            ? 'bg-amber-100 text-amber-800 border border-amber-300' 
                            : 'bg-slate-100 text-slate-700 border border-slate-200'
                        }`}>
                          {d.day}
                        </span>
                        <div>
                          <div className="font-bold text-slate-800 text-[12px]">{dateInfo.fullDate}</div>
                          {dateInfo.isHoliday && (
                            <span className="text-[9px] font-bold text-amber-600 uppercase tracking-tighter">Weekly Off</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Arrive Punch */}
                    <td className="py-2.5 px-3 text-center">
                      <div className="inline-flex flex-col items-center">
                        <input
                          type="text"
                          value={d.arrive || ''}
                          placeholder="00:00"
                          onChange={(e) => onUpdateDay(employee.id, d.day, { arrive: e.target.value })}
                          onBlur={(e) => onUpdateDay(employee.id, d.day, { arrive: normalizeTimeInput(e.target.value) })}
                          className="w-20 px-2 py-1 text-center font-mono font-bold text-xs bg-white border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-400 outline-none shadow-2xs"
                        />
                        {lateMins > 0 && (
                          <span className="text-[9px] font-bold text-amber-600 mt-0.5">
                            +{minutesToTime(lateMins)} Late
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Depart Punch */}
                    <td className="py-2.5 px-3 text-center">
                      <input
                        type="text"
                        value={d.depart || ''}
                        placeholder="00:00"
                        onChange={(e) => onUpdateDay(employee.id, d.day, { depart: e.target.value })}
                        onBlur={(e) => onUpdateDay(employee.id, d.day, { depart: normalizeTimeInput(e.target.value) })}
                        className="w-20 px-2 py-1 text-center font-mono font-bold text-xs bg-white border border-slate-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-400 outline-none shadow-2xs"
                      />
                    </td>

                    {/* Total Worked */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      {workedMins > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className={`font-mono font-black text-xs px-2 py-0.5 rounded-full border ${
                            workedMins >= 510 
                              ? 'text-emerald-800 bg-emerald-50 border-emerald-200' 
                              : workedMins <= 270 
                              ? 'text-amber-800 bg-amber-50 border-amber-200' 
                              : 'text-red-800 bg-red-50 border-red-200'
                          }`}>
                            {minutesToTime(workedMins)}
                          </span>
                          <span className={`text-[9px] mt-0.5 font-medium ${
                            workedMins < 510 && workedMins > 270 ? 'text-red-600 font-bold' : 'text-slate-500'
                          }`}>
                            {workedMins >= 510 ? 'Full Day' : workedMins <= 270 ? 'Half Day' : `Short -${minutesToTime(510 - workedMins)}`}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 font-mono">-</span>
                      )}
                    </td>

                    {/* Machine WorkHrs */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      <div className="inline-flex flex-col items-center">
                        <span className="font-mono text-xs text-slate-600 font-medium">
                          {d.workHrs || '-'}
                        </span>
                        {isMachineGlitch && (
                          <span className="text-[8px] font-bold text-red-600 flex items-center gap-0.5 mt-0.5 bg-red-50 px-1 rounded border border-red-200" title={`Biometric machine logged ${d.workHrs} but punches span ${minutesToTime(workedMins)}!`}>
                            <i className="fas fa-exclamation-triangle"></i> Glitch ({d.workHrs})
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status Selector */}
                    <td className="py-2.5 px-3 text-center">
                      <select
                        value={d.status || ''}
                        onChange={(e) => onUpdateDay(employee.id, d.day, { status: e.target.value })}
                        className={`w-24 px-2 py-1 text-center text-xs font-bold rounded-lg border outline-none cursor-pointer shadow-2xs transition-colors ${
                          d.status === 'P' ? 'text-emerald-700 bg-emerald-50 border-emerald-300 focus:border-emerald-500' :
                          d.status === 'A' ? 'text-red-700 bg-red-50 border-red-300 focus:border-red-500' :
                          d.status === 'WO' ? 'text-amber-700 bg-amber-50 border-amber-300 focus:border-amber-500' :
                          d.status === 'P/2' ? 'text-blue-700 bg-blue-50 border-blue-300 focus:border-blue-500' :
                          d.status === 'POW' ? 'text-purple-700 bg-purple-50 border-purple-300 focus:border-purple-500' :
                          d.status === 'L' ? 'text-orange-700 bg-orange-50 border-orange-300 focus:border-orange-500' :
                          'text-slate-700 bg-white border-slate-300 focus:border-blue-500'
                        }`}
                      >
                        <option value="P">P (Present)</option>
                        <option value="A">A (Absent)</option>
                        <option value="WO">WO (Off)</option>
                        <option value="P/2">P/2 (Half)</option>
                        <option value="POW">POW (Paid Off)</option>
                        <option value="L">L (Leave)</option>
                        {!['P', 'A', 'WO', 'P/2', 'POW', 'L', ''].includes(d.status) && (
                          <option value={d.status}>{d.status}</option>
                        )}
                      </select>
                    </td>

                    {/* Machine Actual OT */}
                    <td className="py-2.5 px-3 text-center font-mono text-xs text-slate-500 font-semibold whitespace-nowrap">
                      {d.actualOt || '00:00'}
                    </td>

                    {/* Calculated OT with quick adjustments */}
                    <td className="py-2.5 px-3 text-center whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <input
                          type="text"
                          value={d.calculatedOt || d.ot || '00:00'}
                          onChange={(e) => onUpdateDay(employee.id, d.day, { calculatedOt: e.target.value })}
                          onBlur={(e) => onUpdateDay(employee.id, d.day, { calculatedOt: normalizeTimeInput(e.target.value) })}
                          className="w-16 px-1.5 py-1 text-center font-mono font-bold text-xs text-blue-800 bg-blue-50/70 border border-blue-200 rounded-md focus:border-blue-500 outline-none shadow-2xs"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const cur = timeToMinutes(d.calculatedOt || d.ot);
                            onUpdateDay(employee.id, d.day, { calculatedOt: minutesToTime(cur + 5) });
                          }}
                          className="px-1.5 py-1 text-[10px] font-bold bg-white hover:bg-slate-100 border border-slate-300 rounded text-slate-700 shadow-2xs cursor-pointer"
                          title="Add 5 minutes"
                        >
                          +5
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const cur = timeToMinutes(d.calculatedOt || d.ot);
                            onUpdateDay(employee.id, d.day, { calculatedOt: minutesToTime(Math.max(0, cur - 5)) });
                          }}
                          className="px-1.5 py-1 text-[10px] font-bold bg-white hover:bg-slate-100 border border-slate-300 rounded text-slate-700 shadow-2xs cursor-pointer"
                          title="Subtract 5 minutes"
                        >
                          -5
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const autoOt = calculateDayOt(d.arrive, d.depart);
                            onUpdateDay(employee.id, d.day, { calculatedOt: autoOt });
                          }}
                          className="px-1.5 py-1 text-[10px] font-bold bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded text-blue-700 shadow-2xs cursor-pointer"
                          title="Reset to formula-calculated OT from punches"
                        >
                          Auto
                        </button>
                      </div>
                    </td>

                    {/* Audit & Quick Fix */}
                    <td className="py-2.5 px-3">
                      {alertInfo ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded border shadow-2xs ${
                            alertInfo.color === 'red'
                              ? 'text-red-800 bg-red-100 border-red-300'
                              : 'text-amber-800 bg-amber-100 border-amber-300'
                          }`}>
                            <i className={`fas ${alertInfo.color === 'red' ? 'fa-exclamation-triangle text-red-600' : 'fa-exclamation-circle text-amber-600'}`}></i>
                            {alertInfo.label}
                          </span>
                          
                          <span className="text-[11px] text-slate-700 font-medium">
                            {alertInfo.reason}
                          </span>

                          {/* Quick Fix Buttons */}
                          {(alertInfo.label === 'STATUS ERROR' || (d.status === 'P/2' && workedMins >= 510)) && (
                            <button
                              type="button"
                              onClick={() => onUpdateDay(employee.id, d.day, { status: 'P' })}
                              className="px-2 py-0.5 text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
                              title="Set status to Present (P)"
                            >
                              <i className="fas fa-check"></i> Fix to P
                            </button>
                          )}

                          {alertInfo.label === 'HALF DAY ERROR' && (
                            <button
                              type="button"
                              onClick={() => onUpdateDay(employee.id, d.day, { status: 'P/2' })}
                              className="px-2 py-0.5 text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 rounded shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
                              title="Set status to Half Day (P/2)"
                            >
                              <i className="fas fa-check"></i> Set to P/2
                            </button>
                          )}

                          {alertInfo.reason.includes('Missing Departure') && (
                            <button
                              type="button"
                              onClick={() => onUpdateDay(employee.id, d.day, { depart: '17:00' })}
                              className="px-2 py-0.5 text-[11px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
                              title="Set standard Departure time to 17:00"
                            >
                              <i className="fas fa-clock"></i> Set 17:00
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                          <i className="fas fa-check-circle"></i> Clean
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 5. Modal Footer */}
        <div className="p-3 sm:px-6 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <i className="fas fa-info-circle text-blue-500"></i>
            <span>All edits auto-save and immediately update totals and main sheet.</span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 shadow-2xs transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const AttendanceRow = React.memo(function AttendanceRow({
  row,
  dayHeaders,
  onSelectEmployee,
  onDayChange,
  onDayOtBlur,
  onCellChange
}) {
  const daysMap = useMemo(() => {
    const map = {};
    (row.days || []).forEach(d => {
      map[d.day] = d;
    });
    return map;
  }, [row.days]);

  return (
    <tr className="hover:bg-slate-50/80 transition-colors">
      <td className="px-3 py-2 font-medium text-slate-900 sticky left-0 bg-white shadow-[1px_0_0_#e2e8f0] z-10">
        <button
          type="button"
          onClick={() => onSelectEmployee(row.id)}
          className="font-bold text-slate-700 hover:text-blue-600 transition-colors cursor-pointer"
          title="Click to open employee attendance modal"
        >
          {row.empCode}
        </button>
      </td>
      <td className="px-3 py-2 font-bold text-slate-900 sticky left-[80px] bg-white shadow-[1px_0_0_#e2e8f0] z-10">
        <button 
          type="button"
          onClick={() => onSelectEmployee(row.id)}
          className="text-left font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 group cursor-pointer"
          title="Click to open full audit & edit modal for this employee"
        >
          <span>{row.name}</span>
          <i className="fas fa-external-link-alt text-[9px] opacity-40 group-hover:opacity-100 transition-opacity text-blue-500"></i>
        </button>
      </td>
      
      {/* Day Cells */}
      {dayHeaders.map(dayNum => {
        const dayData = daysMap[dayNum] || { status: "", actualOt: "00:00", calculatedOt: "00:00", ot: "00:00", arrive: "", depart: "" };
        const alertInfo = getDayAlertInfo(dayData);
        
        return (
          <td key={dayNum} className={`px-1 py-1 align-top border-l border-slate-100 ${alertInfo?.color === 'red' ? 'bg-red-50/40' : alertInfo?.color === 'amber' ? 'bg-amber-50/40' : ''}`}>
            <div 
              className={`flex flex-col gap-1 items-center p-1 rounded-lg transition-all ${
                alertInfo?.color === 'red' 
                  ? 'border-2 border-red-500 bg-red-50/90 shadow-md ring-2 ring-red-400/30' 
                  : alertInfo?.color === 'amber'
                  ? 'border-2 border-amber-500 bg-amber-50/90 shadow-md ring-2 ring-amber-400/30'
                  : 'border border-transparent'
              }`}
              title={alertInfo ? `⚠️ ${alertInfo.reason}` : undefined}
            >
              {/* Alert Badge */}
              {alertInfo && (
                <div 
                  className={`w-[56px] text-[6.5px] font-black rounded px-0.5 py-0.5 text-center flex items-center justify-center gap-0.5 leading-none shadow-2xs ${
                    alertInfo.color === 'red'
                      ? 'text-red-700 bg-red-100 border border-red-400/80'
                      : 'text-amber-800 bg-amber-100 border border-amber-400'
                  }`}
                  title={alertInfo.reason}
                >
                  <i className={`fas ${alertInfo.color === 'red' ? 'fa-exclamation-triangle text-red-600' : 'fa-exclamation-circle text-amber-600'} text-[7px]`}></i>
                  <span>{alertInfo.label}</span>
                </div>
              )}

              {/* Status Dropdown */}
              <select 
                value={dayData.status || ""} 
                onChange={(e) => onDayChange(row.id, dayNum, 'status', e.target.value)}
                className={`w-[56px] px-0.5 py-0.5 text-center text-[10px] font-bold border rounded outline-none cursor-pointer transition-colors
                  ${dayData.status === 'P' ? 'text-emerald-700 bg-emerald-50 border-emerald-300 focus:border-emerald-500' : 
                    dayData.status === 'A' ? 'text-red-700 bg-red-50 border-red-300 focus:border-red-500' : 
                    dayData.status === 'WO' ? 'text-amber-700 bg-amber-50 border-amber-300 focus:border-amber-500' : 
                    dayData.status === 'POW' ? 'text-purple-700 bg-purple-50 border-purple-300 focus:border-purple-500' : 
                    dayData.status === 'P/2' ? 'text-blue-700 bg-blue-50 border-blue-300 focus:border-blue-500' : 
                    dayData.status === 'L' ? 'text-orange-700 bg-orange-50 border-orange-300 focus:border-orange-500' : 
                    'text-slate-700 bg-white border-slate-200 focus:border-blue-500'}`}
                title={`Day ${dayNum} Status: ${dayData.status}`}
              >
                <option value="P" className="text-emerald-700 font-bold bg-white">P</option>
                <option value="A" className="text-red-700 font-bold bg-white">A</option>
                <option value="WO" className="text-amber-700 font-bold bg-white">WO</option>
                <option value="P/2" className="text-blue-700 font-bold bg-white">P/2</option>
                <option value="POW" className="text-purple-700 font-bold bg-white">POW</option>
                <option value="L" className="text-orange-700 font-bold bg-white">L</option>
                {!['P', 'A', 'WO', 'P/2', 'POW', 'L', ''].includes(dayData.status) && (
                  <option value={dayData.status} className="text-slate-700 font-bold bg-white">{dayData.status}</option>
                )}
              </select>

              {/* Arrived & Departure Time */}
              <div 
                className="w-[56px] px-1 py-0.5 text-center text-[8px] bg-slate-50 border border-slate-200 rounded select-none cursor-default font-mono leading-tight"
                title={`Day ${dayNum} Arrive: ${dayData.arrive || '--:--'} | Depart: ${dayData.depart || '--:--'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[7px] text-emerald-600 font-sans font-bold">A:</span>
                  <span className="font-semibold text-slate-700">{dayData.arrive || '--:--'}</span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200/60 pt-0.5 mt-0.5">
                  <span className="text-[7px] text-indigo-600 font-sans font-bold">D:</span>
                  <span className="font-semibold text-slate-700">{dayData.depart || '--:--'}</span>
                </div>
              </div>

              {/* Actual Machine OT */}
              <div 
                className="w-[56px] px-1 py-0.5 text-center text-[9px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded select-none cursor-default"
                title={`Day ${dayNum} Actual Machine OT: ${dayData.actualOt || '00:00'}`}
              >
                <span className="text-[7.5px] text-slate-400 font-bold mr-0.5 uppercase">Act:</span>
                {dayData.actualOt || '00:00'}
              </div>

              {/* Calculated OT */}
              <input 
                type="text" 
                value={dayData.calculatedOt || dayData.ot || '00:00'} 
                onChange={(e) => onDayChange(row.id, dayNum, 'calculatedOt', e.target.value)}
                onBlur={() => onDayOtBlur(row.id, dayNum)}
                className="w-[56px] px-1 py-0.5 text-center text-[10px] font-bold text-blue-700 bg-blue-50/70 border border-blue-200 rounded focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500 outline-none"
                title={`Day ${dayNum} Calculated OT (Editable) - Click to change`}
                placeholder="00:00"
              />
            </div>
          </td>
        );
      })}

      {/* Summaries */}
      <td className="px-3 py-2 text-center border-l border-slate-100 bg-slate-50/30">
        <input 
          type="text" 
          value={row.paidDays} 
          onChange={(e) => onCellChange(row.id, 'paidDays', e.target.value)}
          className="w-12 px-1 py-1 text-center text-xs font-bold border border-slate-200 rounded outline-none focus:border-blue-500"
          title="Paid Days"
        />
      </td>
      <td className="px-3 py-2 text-center text-xs font-semibold text-slate-500 bg-slate-50 border-l border-slate-100" title="Total Actual OT">
        {row.totalActualOt || '00:00'}
      </td>
      <td className="px-3 py-2 text-center bg-blue-50/30 border-l border-blue-100">
        <input 
          type="text" 
          value={row.totalCalculatedOt || row.totalOvertime} 
          onChange={(e) => onCellChange(row.id, 'totalCalculatedOt', e.target.value)}
          className="w-16 px-1 py-1 text-center text-xs font-bold text-blue-700 border border-blue-200 rounded outline-none focus:border-blue-500"
          title="Total Calculated OT (Editable)"
        />
      </td>
      <td className="px-3 py-2 text-center font-mono font-bold text-xs text-amber-700 bg-amber-50/30 border-l border-amber-100" title="Total Short Hours (Worked under 8:30)">
        {row.totalShortHrs || '00:00'}
      </td>
    </tr>
  );
});

export default function AttendanceManager() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Database states
  const [savedMonths, setSavedMonths] = useState([]);
  const [currentMonthYear, setCurrentMonthYear] = useState("");
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  // Modal & Auto-save states
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const autoSaveTimerRef = useRef(null);

  // Search & Pagination states for high-performance rendering
  const [searchQuery, setSearchQuery] = useState("");
  const [filterIssuesOnly, setFilterIssuesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25); // 25, 50, or 'all'

  const dayHeaders = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  const showNotification = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Saving works only through refs, never through render-time state. Several edit
  // handlers are memoized with useCallback([]), so anything they close over (such as
  // currentMonthYear) stays at its first-render value; reading state here made
  // those edits silently skip the save.
  const monthRef = useRef("");
  monthRef.current = currentMonthYear;
  const pendingSaveRef = useRef(null); // latest unsaved { month, data }
  const savePromiseRef = useRef(null); // in-flight save loop, so saves never overlap
  const retryTimerRef = useRef(null);
  const versionRef = useRef(0);   // server version of the month we are editing
  const baseDataRef = useRef(null); // what the server held when we last loaded/saved

  // Writes the newest pending snapshot; edits made during a save are picked up by the
  // loop, so the last edit always wins and requests can't land out of order.
  const flushSave = () => {
    if (savePromiseRef.current) return savePromiseRef.current;
    savePromiseRef.current = (async () => {
      let ok = true;
      let conflicts = 0;
      try {
        while (pendingSaveRef.current) {
          const job = pendingSaveRef.current;
          setAutoSaveStatus("saving");
          try {
            const res = await api.post("/attendance/save", {
              month_year: job.month,
              data: job.data,
              base_version: versionRef.current
            });
            versionRef.current = res.data?.version ?? versionRef.current;
            baseDataRef.current = job.data;
            if (pendingSaveRef.current === job) pendingSaveRef.current = null;
          } catch (err) {
            // Someone else saved this month since we opened it: keep their changes,
            // lay ours on top, and save again against the new version.
            if (err.response?.status === 409 && Array.isArray(err.response.data?.data) && conflicts < 5) {
              conflicts += 1;
              const latestLocal = pendingSaveRef.current || job;
              const serverRows = normalizeAndRoundData(err.response.data.data);
              const merged = mergeLocalChanges(baseDataRef.current, latestLocal.data, serverRows);
              versionRef.current = err.response.data.version;
              baseDataRef.current = serverRows;
              pendingSaveRef.current = { month: latestLocal.month, data: merged };
              if (monthRef.current === latestLocal.month) setData(merged);
              showNotification("Another user saved this month - their changes were merged with yours.", "info");
              continue;
            }
            console.error("Auto-save error:", err);
            ok = false;
            setAutoSaveStatus("error");
            const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to save data to database";
            setError(`Save failed (will retry automatically): ${errMsg}`);
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = setTimeout(() => flushSave(), 5000);
            break;
          }
        }
        if (ok) {
          setError(null);
          setAutoSaveStatus("saved");
          setTimeout(() => setAutoSaveStatus((s) => (s === "saved" ? "idle" : s)), 3000);
        }
      } finally {
        savePromiseRef.current = null;
      }
      return ok;
    })();
    return savePromiseRef.current;
  };

  const triggerAutoSave = (latestData) => {
    const month = monthRef.current;
    if (!month || !latestData || latestData.length === 0) return;
    pendingSaveRef.current = { month, data: latestData };
    setAutoSaveStatus("pending");
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    // Debounce: do NOT synchronously update state during typing
    autoSaveTimerRef.current = setTimeout(() => flushSave(), 800);
  };

  // Save anything still waiting before the data on screen is replaced
  const flushPendingNow = async () => {
    clearTimeout(autoSaveTimerRef.current);
    if (pendingSaveRef.current || savePromiseRef.current) await flushSave();
  };

  const totalEmployeesWithIssues = useMemo(() => {
    return data.filter(emp => (emp.days || []).some(d => getDayAlertInfo(d) !== null)).length;
  }, [data]);

  const filteredData = useMemo(() => {
    let list = data;
    if (filterIssuesOnly) {
      list = list.filter(emp => (emp.days || []).some(d => getDayAlertInfo(d) !== null));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(emp => 
        (emp.name || '').toLowerCase().includes(q) || 
        String(emp.empCode || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [data, filterIssuesOnly, searchQuery]);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredData.length / pageSize) || 1;
  const paginatedData = useMemo(() => {
    if (pageSize === 'all') return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    // Leaving the page must not drop a save that is still debounced
    const warnIfUnsaved = (e) => {
      if (pendingSaveRef.current || savePromiseRef.current) {
        flushSave();
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warnIfUnsaved);
    return () => {
      window.removeEventListener("beforeunload", warnIfUnsaved);
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (pendingSaveRef.current) flushSave();
    };
  }, []);

  const fetchSavedMonths = async () => {
    try {
      const res = await api.get("/attendance/months");
      setSavedMonths(res.data || []);
    } catch (err) {
      console.error("Error fetching months:", err);
    }
  };

  useEffect(() => {
    fetchSavedMonths();
  }, []);

  // Notice saves made by other users/windows and pull them in while we have nothing
  // unsaved, so we never keep editing (and later saving) a stale copy.
  useEffect(() => {
    if (!currentMonthYear) return undefined;
    const timer = setInterval(async () => {
      if (pendingSaveRef.current || savePromiseRef.current) return;
      try {
        const v = await api.get(`/attendance/months/${encodeURIComponent(currentMonthYear)}/version`);
        if (pendingSaveRef.current || savePromiseRef.current) return;
        if (v.data?.version === undefined || v.data.version === versionRef.current) return;
        const res = await api.get(`/attendance/months/${encodeURIComponent(currentMonthYear)}`);
        if (pendingSaveRef.current || savePromiseRef.current) return; // edited meanwhile: the save will merge
        const fresh = normalizeAndRoundData(res.data.data);
        versionRef.current = res.data.version ?? versionRef.current;
        baseDataRef.current = fresh;
        setData(fresh);
        showNotification("Updated with changes saved by another user.", "info");
      } catch (err) {
        // transient network problem: try again on the next tick
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [currentMonthYear]);

  const loadMonthData = async (monthYear) => {
    if (!monthYear) {
        setData([]);
        setCurrentMonthYear("");
        return;
    }
    
    setLoading(true);
    try {
      await flushPendingNow();
      const res = await api.get(`/attendance/months/${encodeURIComponent(monthYear)}`);
      const cleanData = normalizeAndRoundData(res.data.data);
      versionRef.current = res.data.version ?? 0;
      baseDataRef.current = cleanData;
      monthRef.current = monthYear;
      setData(cleanData);
      setCurrentMonthYear(monthYear);
      showNotification(`Loaded data for ${monthYear}`);
    } catch (err) {
      console.error("Error loading month:", err);
      setError("Failed to load saved data");
    } finally {
      setLoading(false);
    }
  };

  const saveToDatabase = async (overrideData = null) => {
    const toSave = Array.isArray(overrideData) ? overrideData : data;
    if (!currentMonthYear || !toSave || toSave.length === 0) {
      setError("No valid month or data to save.");
      return;
    }
    
    setSaving(true);
    clearTimeout(autoSaveTimerRef.current);
    pendingSaveRef.current = { month: currentMonthYear, data: toSave };
    try {
      const ok = await flushSave();
      if (ok) {
        showNotification(`Successfully saved ${currentMonthYear} to database!`);
        fetchSavedMonths(); // refresh list in case it's a new month
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    await flushPendingNow();
    setData([]);
    setCurrentMonthYear("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/attendance/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (response.data && response.data.data) {
        const uploadedMonth = response.data.month_year || "Unknown Month";
        // The next edit auto-saves the whole month, so a fresh file would silently
        // replace corrections already saved for that month.
        if (savedMonths.some((m) => m.month_year === uploadedMonth)) {
          const useFile = window.confirm(
            `${uploadedMonth} already has saved data with your corrections.\n\n` +
            `OK = replace it with this file (corrections are lost; the old version is kept in backup history)\n` +
            `Cancel = keep the saved data and load it instead`
          );
          if (!useFile) {
            await loadMonthData(uploadedMonth);
            return;
          }
        }
        const cleanData = normalizeAndRoundData(response.data.data);
        // Replacing is intentional here, so take the server's current version as the base
        let currentVersion = 0;
        try {
          const v = await api.get(`/attendance/months/${encodeURIComponent(uploadedMonth)}/version`);
          currentVersion = v.data?.version ?? 0;
        } catch (e) { /* 404: month not saved yet */ }
        versionRef.current = currentVersion;
        baseDataRef.current = cleanData;
        monthRef.current = uploadedMonth;
        setData(cleanData);
        setCurrentMonthYear(uploadedMonth);
        showNotification("File processed successfully!");
      }
    } catch (err) {
      console.error("Upload Error:", err);
      setError(err.response?.data?.error || "Failed to process the file");
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const updateEmployeeDay = (id, dayNum, updates) => {
    setData(prev => {
      const updatedList = prev.map(row => {
        if (row.id !== id) return row;

        const newDays = (row.days || []).map(d => {
          if (d.day !== dayNum) return d;
          const merged = { ...d, ...updates };

          // If arrive or depart changed and calculatedOt wasn't explicitly passed, recalculate calculatedOt
          if (('arrive' in updates || 'depart' in updates) && !('calculatedOt' in updates)) {
            const autoOt = calculateDayOt(merged.arrive, merged.depart);
            merged.calculatedOt = autoOt;
            merged.ot = autoOt;
          }
          if ('calculatedOt' in updates) {
            merged.ot = updates.calculatedOt;
          }

          return merged;
        });

        return recomputeEmployeeTotals(row, newDays);
      });

      triggerAutoSave(updatedList);
      return updatedList;
    });
  };

  const handleSelectEmployee = useCallback((id) => {
    setSelectedEmployeeId(id);
  }, []);

  const handleCellChange = useCallback((id, field, value) => {
    setData(prev => {
      const updatedList = prev.map(row => 
        row.id === id ? { ...row, [field]: value } : row
      );
      triggerAutoSave(updatedList);
      return updatedList;
    });
  }, []);

  const handleDayChange = useCallback((id, dayNum, field, value) => {
    updateEmployeeDay(id, dayNum, { [field]: value });
  }, []);

  const handleDayOtBlur = useCallback((id, dayNum) => {
    setData(prev => {
      const emp = prev.find(e => e.id === id);
      if (!emp) return prev;
      const day = (emp.days || []).find(d => d.day === dayNum);
      if (!day) return prev;
      const rawStr = String(day.calculatedOt || day.ot || '').trim();
      let newOt = '00:00';
      if (rawStr && rawStr !== '0' && rawStr !== '00:00' && rawStr !== '0:0') {
        const rawMins = timeToMinutes(rawStr);
        if (rawMins > 0) newOt = minutesToTime(rawMins);
      }
      const updatedList = prev.map(row => {
        if (row.id !== id) return row;
        const newDays = (row.days || []).map(d => d.day === dayNum ? { ...d, calculatedOt: newOt, ot: newOt } : d);
        const totalCalcMins = newDays.reduce((acc, d) => acc + timeToMinutes(d.calculatedOt || d.ot), 0);
        return { ...row, days: newDays, totalCalculatedOt: minutesToTime(totalCalcMins), totalOvertime: minutesToTime(totalCalcMins) };
      });
      triggerAutoSave(updatedList);
      return updatedList;
    });
  }, []);

  const handleRecalculateEntireMonthOt = () => {
    if (!data || data.length === 0) return;
    let daysUpdated = 0;
    const updatedList = data.map(emp => {
      const newDays = (emp.days || []).map(d => {
        if (d.arrive && d.depart) {
          const autoOt = calculateDayOt(d.arrive, d.depart);
          daysUpdated++;
          return { ...d, calculatedOt: autoOt, ot: autoOt };
        }
        return d;
      });
      const totalCalcMins = newDays.reduce((acc, d) => acc + timeToMinutes(d.calculatedOt || d.ot), 0);
      return {
        ...emp,
        days: newDays,
        totalCalculatedOt: minutesToTime(totalCalcMins),
        totalOvertime: minutesToTime(totalCalcMins)
      };
    });
    setData(updatedList);
    triggerAutoSave(updatedList);
    showNotification(`Recalculated overtime for all employees with 5:30 buffer & 5-min round!`);
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      showNotification("No attendance records match the current filters.", "error");
      return;
    }
    
    const exportData = filteredData.map(item => {
      return {
        "EmpCode": item.empCode,
        "Name": item.name,
        "Total Present": item.present ?? 0,
        "Total Absent": item.absent ?? 0,
        "Audited OT Hours": item.totalCalculatedOt || item.totalOvertime || "00:00",
        "Total Late Hours": item.lateHrs || "00:00",
        "Weekly Off": item.wo ?? 0,
      };
    });

    try {
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");

      const safeMonthYear = (currentMonthYear || "Export")
        .replace(/[^a-z0-9_-]/gi, "_");
      XLSX.writeFile(workbook, `Attendance_${safeMonthYear}.xlsx`, {
        compression: true,
      });
      showNotification(`Exported ${exportData.length} attendance record${exportData.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Attendance Excel export error:", err);
      showNotification("Failed to export the Excel file.", "error");
    }
  };

  const selectedEmployee = data.find(e => e.id === selectedEmployeeId) || null;

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:px-6 lg:px-8 pb-32 relative">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-2xl text-white font-medium flex items-center gap-2 transition-all ${notification.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'}`}>
          <i className={`fas ${notification.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i>
          <span>{notification.msg}</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center text-xl shadow-md">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  Biometric Attendance Manager
                </h1>
                <p className="text-sm text-slate-500">
                  Day-wise attendance and OT editor. Upload or load previous months.
                </p>
              </div>
            </div>

            {/* Load Saved Month Dropdown */}
            {savedMonths.length > 0 && (
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg border border-slate-200">
                <i className="fas fa-database text-slate-400 pl-2"></i>
                <select 
                  className="bg-transparent border-none text-sm font-medium text-slate-700 outline-none cursor-pointer pr-4"
                  onChange={(e) => loadMonthData(e.target.value)}
                  value={currentMonthYear || ""}
                >
                  <option value="">-- Load Saved Month --</option>
                  {savedMonths.map((m, i) => (
                    <option key={i} value={m.month_year}>{m.month_year}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!data.length && !loading && (
            <div 
              className={`mt-6 border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center transition-colors ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <i className="fas fa-file-excel text-4xl text-slate-400 mb-3"></i>
              <p className="text-slate-600 font-medium mb-1">Drag and drop your MnPerformance.xls file here</p>
              <p className="text-slate-400 text-sm mb-4">or click to browse from your computer</p>
              
              <label className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold cursor-pointer shadow-sm transition-all">
                Select Excel File
                <input 
                  type="file" 
                  className="hidden" 
                  accept=".xls,.xlsx" 
                  onChange={handleChange} 
                />
              </label>
            </div>
          )}

          {loading && (
            <div className="mt-10 flex flex-col items-center justify-center text-blue-600">
              <i className="fas fa-spinner fa-spin text-4xl mb-3"></i>
              <p className="font-medium animate-pulse">Extracting records...</p>
            </div>
          )}

          {error && (
            <div className="mt-6 bg-red-50 text-red-700 p-4 rounded-lg border border-red-200 flex items-center gap-3">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-sm bg-red-100 px-3 py-1 rounded hover:bg-red-200"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>

      {data.length > 0 && (
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Top Toolbar */}
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
              {/* Title, Month & Auto-save Status */}
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="font-bold text-slate-800 text-sm sm:text-base whitespace-nowrap">
                  Attendance Records ({filteredData.length}{filteredData.length !== data.length ? ` of ${data.length}` : ''})
                </h2>
                {currentMonthYear && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">
                    {currentMonthYear}
                  </span>
                )}
                {autoSaveStatus === 'saving' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200 animate-pulse">
                    <i className="fas fa-spinner fa-spin text-blue-600"></i> Auto-saving...
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <i className="fas fa-check-circle text-emerald-600"></i> Auto-saved
                  </span>
                )}
                {autoSaveStatus === 'pending' && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    <i className="fas fa-pen text-amber-600"></i> Unsaved changes...
                  </span>
                )}
                {autoSaveStatus === 'error' && (
                  <button
                    onClick={() => flushSave()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-300 cursor-pointer"
                    title="Saving failed. Click to retry now"
                  >
                    <i className="fas fa-exclamation-triangle text-red-600"></i> NOT SAVED - retry
                  </button>
                )}

                {/* Search Box */}
                <div className="relative min-w-[180px]">
                  <i className="fas fa-search absolute left-2.5 top-2 text-slate-400 text-xs"></i>
                  <input 
                    type="text"
                    placeholder="Search name / #code..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-7 pr-6 py-1 text-xs bg-white border border-slate-300 rounded-lg outline-none focus:border-blue-500 w-full shadow-2xs font-medium"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs cursor-pointer"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                {/* Filter Issues Only Button */}
                <button
                  type="button"
                  onClick={() => { setFilterIssuesOnly(prev => !prev); setCurrentPage(1); }}
                  className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs ${
                    filterIssuesOnly
                      ? 'bg-red-600 text-white border-red-600'
                      : totalEmployeesWithIssues > 0
                      ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                      : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                  }`}
                  title="Filter employees who have attendance errors or short-hours"
                >
                  <i className="fas fa-exclamation-triangle"></i>
                  Issues ({totalEmployeesWithIssues})
                </button>
              </div>
              
              {/* Pagination & Global Actions */}
              <div className="flex flex-wrap items-center justify-between lg:justify-end gap-2">
                {/* Page Size Selector */}
                <div className="flex items-center gap-1 text-xs text-slate-600 font-semibold">
                  <span>Show:</span>
                  <select 
                    value={pageSize} 
                    onChange={(e) => { setPageSize(e.target.value === 'all' ? 'all' : Number(e.target.value)); setCurrentPage(1); }}
                    className="text-xs font-semibold bg-white border border-slate-300 rounded-lg px-2 py-1 outline-none cursor-pointer shadow-2xs"
                  >
                    <option value={20}>20 / page</option>
                    <option value={35}>35 / page</option>
                    <option value={50}>50 / page</option>
                    <option value="all">All ({filteredData.length})</option>
                  </select>
                </div>

                {/* Top Pagination Buttons */}
                {pageSize !== 'all' && totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-xs font-bold bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed shadow-2xs"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <span className="text-xs font-bold text-slate-700 px-1 whitespace-nowrap">
                      {currentPage}/{totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-xs font-bold bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed shadow-2xs"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                )}

                <button 
                  onClick={() => {
                    setData([]);
                    setCurrentMonthYear("");
                  }} 
                  className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 shadow-2xs cursor-pointer"
                >
                  Close
                </button>
                <button 
                  onClick={() => saveToDatabase()}
                  disabled={saving}
                  className="px-3 py-1.5 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-1.5 shadow-sm disabled:opacity-50 cursor-pointer"
                >
                  {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                  Save
                </button>
                <button 
                  type="button"
                  onClick={handleRecalculateEntireMonthOt}
                  className="px-3 py-1.5 text-xs font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-lg flex items-center gap-1.5 shadow-2xs cursor-pointer transition-colors"
                  title="Recalculate OT for all employees using company rule (5:30 buffer + 5-min round)"
                >
                  <i className="fas fa-calculator text-amber-600"></i> Recalc OT
                </button>
                <button 
                  onClick={exportToExcel}
                  className="px-3 py-1.5 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <i className="fas fa-download"></i> Export Excel
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-slate-600">
                <thead className="text-[10px] uppercase bg-slate-100 text-slate-700 sticky top-0 shadow-sm z-10">
                  <tr>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap sticky left-0 bg-slate-100 z-20 shadow-[1px_0_0_#e2e8f0]">Emp Code</th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap sticky left-[80px] bg-slate-100 z-20 shadow-[1px_0_0_#e2e8f0] min-w-[150px]">Name</th>
                    
                    {/* Day Headers */}
                    {dayHeaders.map(dayNum => (
                      <th key={dayNum} className="px-1 py-2 font-semibold text-center min-w-[70px] border-l border-slate-200">
                        <div className="text-slate-800 font-bold">{dayNum}</div>
                        <div className="text-[7px] text-slate-400 font-normal tracking-tighter">In-Out/OT</div>
                      </th>
                    ))}
                    
                    <th className="px-3 py-3 font-semibold whitespace-nowrap text-center border-l border-slate-200 bg-slate-100">Total Paid</th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap text-center bg-slate-100 text-slate-500">Actual OT</th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap text-center bg-blue-50 text-blue-800 border-l border-blue-100">Calc OT</th>
                    <th className="px-3 py-3 font-semibold whitespace-nowrap text-center bg-amber-50 text-amber-800 border-l border-amber-100">Short Hrs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={36} className="py-12 text-center text-slate-400 font-medium">
                        <i className="fas fa-search text-2xl mb-2 block opacity-40"></i>
                        No employees found matching your search / filter.
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((row) => (
                      <AttendanceRow
                        key={row.id}
                        row={row}
                        dayHeaders={dayHeaders}
                        onSelectEmployee={handleSelectEmployee}
                        onDayChange={handleDayChange}
                        onDayOtBlur={handleDayOtBlur}
                        onCellChange={handleCellChange}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination & Summary Bar */}
            <div className="p-3 sm:px-6 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="text-slate-500 font-medium">
                Showing <span className="font-bold text-slate-700">{paginatedData.length > 0 ? (pageSize === 'all' ? 1 : (currentPage - 1) * pageSize + 1) : 0}</span> to <span className="font-bold text-slate-700">{pageSize === 'all' ? filteredData.length : Math.min(currentPage * pageSize, filteredData.length)}</span> of <span className="font-bold text-slate-700">{filteredData.length}</span> employees
                {filteredData.length !== data.length && (
                  <span className="text-blue-600 ml-1 font-semibold">(filtered from {data.length} total)</span>
                )}
              </div>

              {pageSize !== 'all' && totalPages > 1 && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-2.5 py-1 text-xs font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                    title="First page"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-xs font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs flex items-center gap-1"
                  >
                    <i className="fas fa-chevron-left text-[10px]"></i> Prev
                  </button>
                  <span className="px-2 font-bold text-slate-700">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-xs font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs flex items-center gap-1"
                  >
                    Next <i className="fas fa-chevron-right text-[10px]"></i>
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-2.5 py-1 text-xs font-semibold bg-white border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
                    title="Last page"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. Individual Employee Audit & Edit Modal */}
      {selectedEmployee && (
        <EmployeeAuditModal
          employee={selectedEmployee}
          allEmployees={data}
          onSelectEmployee={(newId) => setSelectedEmployeeId(newId)}
          onClose={() => setSelectedEmployeeId(null)}
          onUpdateDay={updateEmployeeDay}
          currentMonthYear={currentMonthYear}
          autoSaveStatus={autoSaveStatus}
          showNotification={showNotification}
        />
      )}
    </div>
  );
}
